import { IconButton } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { ArrowBackRectangleIcon, DataTable, Typography } from '@uy3/web-components';
import { formatCurrencyInCents } from 'helpers/formats/Currency';
import { OptionFilterType } from './AccountStatementTabContainer';
import { StatementQueryDataType } from 'services/accounts/bankAccount';
import { iconSx, ToastType } from 'contexts/apiRequestContext';
import { hasCustomPermission } from 'helpers/methods/GenericMethods';
import { useUserPermissionData } from 'contexts/userContext';
import { isValidString } from 'helpers';
import { Fragment } from 'react/jsx-runtime';
import { TooltipComponent } from 'components/Tooltip/Tooltop';
import { useTenant } from 'contexts/tenantContext';
import moment from 'moment';
import { StatementFiltersContainer } from 'components/DataTableFilters/StatementFilters/StatementFiltersContainer';
import { FieldValues } from 'react-hook-form';
import { useEffect } from 'react';

type AccountStatementTabListProps = {
    queryData: StatementQueryDataType[];
    setPeriodSelected: React.Dispatch<React.SetStateAction<string>>;
    setDateFilter: React.Dispatch<React.SetStateAction<FieldValues>>;
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;
    setPage: (page: number) => void;
    page: number;
    rowCount: number;
    setToast: React.Dispatch<React.SetStateAction<ToastType>>;
    setRowData: (rowData: any) => void;
    operatorHasPerm: boolean;
    setStatementDateType: React.Dispatch<React.SetStateAction<OptionFilterType>>;
    statementDateType: OptionFilterType;
    setOrderBy: React.Dispatch<React.SetStateAction<GridSortModel>>;
    dateFilter: FieldValues;
};

const AccountStatementTabList = ({
    queryData,
    setDateFilter,
    setPeriodSelected,
    setPage,
    setRowsPerPage,
    rowsPerPage,
    page,
    setToast,
    setRowData,
    rowCount,
    operatorHasPerm,
    setStatementDateType,
    statementDateType,
    dateFilter,
    setOrderBy,
}: AccountStatementTabListProps) => {
    const { hasPermission } = useUserPermissionData();
    const { isRootTenancy } = useTenant();
    const hasBankAccountPerm = hasCustomPermission('BankAccount', 'Update', hasPermission);

    const checkInformationBedoreShowing = (value: string): string => {
        if (isValidString(value)) return value;
        return 'N/D';
    };

    var columnsAccountStatementTabList: GridColDef[] = [
        {
            field: 'issueDate',
            headerName: 'Data do lançamento',
            hideSortIcons: true,
            minWidth: 180,
            renderCell: ({ value }) => {
                const formattedDate = new Date(value).toLocaleString('pt-BR');
                return value && formattedDate;
            },
        },
        {
            field: 'accountingDate',
            headerName: 'Data contábil',
            hideSortIcons: true,
            hide: !isRootTenancy,
            minWidth: 180,
            renderCell: ({ value }) => {
                const formattedDate = moment(new Date(value)).format('DD/MM/YYYY');
                return value && formattedDate;
            },
        },
        {
            field: 'description',
            headerName: 'Descrição',
            hideSortIcons: true,
            flex: 1,
            renderCell: (cellValues) => {
                return checkInformationBedoreShowing(cellValues.row.description);
            },
        },
        {
            field: 'information',
            headerName: 'Complemento',
            hideSortIcons: true,
            flex: 2,
            renderCell: (cellValues) => {
                return checkInformationBedoreShowing(cellValues.row.information);
            },
        },
        {
            field: 'counterpart',
            headerName: 'Contraparte',
            hideSortIcons: true,
            flex: 2,
            renderCell: (cellValues) => {
                return checkInformationBedoreShowing(cellValues.row.counterpart);
            },
        },
        {
            field: 'amount',
            headerName: 'Valor',
            hideSortIcons: true,
            flex: 1,
            renderCell: (cellValues) => {
                return cellValues.row.movement === 'DEBIT' ? (
                    <Typography
                        variant="h6"
                        fontWeight="400"
                        fontFamily='"Lato", sans-serif'
                        color="#F50057"
                    >
                        - {formatCurrencyInCents(cellValues.row.amount)}
                    </Typography>
                ) : (
                    <Typography
                        variant="h6"
                        fontWeight="400"
                        fontFamily='"Lato", sans-serif'
                        color="#00CD2D"
                    >
                        + {formatCurrencyInCents(cellValues.row.amount)}
                    </Typography>
                );
            },
        },
        {
            field: 'balances',
            headerName: 'Ações',
            hideSortIcons: true,
            minWidth: 100,
            editable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (cellValues) => {
                const isPixReceived =
                    cellValues.row.movement === 'CREDIT' &&
                    cellValues.row.description === 'Pix Recebido';

                const showBtnRefund = operatorHasPerm && isPixReceived && hasBankAccountPerm;

                return (
                    <Fragment>
                        {showBtnRefund && (
                            <TooltipComponent title="Estorno" placement="top">
                                <IconButton onClick={() => setRowData(cellValues.row)}>
                                    <ArrowBackRectangleIcon sx={iconSx} />
                                </IconButton>
                            </TooltipComponent>
                        )}
                    </Fragment>
                );
            },
        },
    ];

    const updatePaginationText = (paginationElement: Element) => {
        const text = paginationElement.textContent || '';
        const updatedText = text.split(' de ')[0];
        paginationElement.textContent = updatedText;
    };

    useEffect(() => {
        const paginationElement = document.querySelector('.MuiTablePagination-displayedRows');

        if (paginationElement) {
            updatePaginationText(paginationElement);

            const mutation = new MutationObserver(() => updatePaginationText(paginationElement));

            mutation.observe(paginationElement, {
                childList: true,
                characterData: true,
                subtree: true,
            });

            return () => mutation.disconnect();
        }
    }, [page, rowsPerPage, queryData]);

    return (
        <>
            <DataTable
                filterComponent={{
                    showFilters: true,
                    componentFilter: (
                        <StatementFiltersContainer
                            setPeriodSelected={setPeriodSelected}
                            setDateFilter={setDateFilter}
                            setToast={setToast}
                            statementDateType={statementDateType}
                            setStatementDateType={setStatementDateType}
                            dateFilter={dateFilter}
                        />
                    ),
                }}
                sx={{
                    '& .MuiTablePagination-displayedRows': {
                        fontSize: '14px',
                    },
                }}
                columns={columnsAccountStatementTabList}
                rowsPerPageOptions={[5, 10, 20, 50]}
                rows={queryData}
                page={page}
                rowCount={rowCount}
                rowsPerPage={rowsPerPage}
                setPage={setPage}
                enableJumpAction={false}
                setRowsPerPage={setRowsPerPage}
                onSortModelChange={(model) => setOrderBy(model)}
            />
        </>
    );
};

export default AccountStatementTabList;
